import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { Permission, UserRole } from '@manpower/shared';
import { SUPPLIER, UserRole as Role } from '@manpower/shared';
import { useAuth } from '@/auth/AuthProvider';
import { BrandMark, Icon, cx, type IconName } from '@/components/primitives';
import { GlobalSearch } from '@/components/GlobalSearch';
import { NotificationBell } from '@/components/NotificationBell';
import { DensityProvider } from '@/design/density';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Omitted means "any signed-in user holding the surrounding role". */
  permission?: Permission;
  end?: boolean;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

/**
 * Sidebar navigation, grouped by what the person is trying to do.
 *
 * A flat top bar cannot carry the back office's ten sections — at laptop widths
 * the labels either wrap and double the header height, or clip. Grouping into a
 * sidebar also puts related work together: approvals sit next to the documents
 * that inform them, and the recruitment desk's day-to-day is one block.
 */
const NAV_BY_ROLE: Record<UserRole, NavGroup[]> = {
  SEEKER: [
    { items: [{ to: '/seeker', label: 'Dashboard', icon: 'home', end: true }] },
    {
      label: 'Find work',
      items: [
        { to: '/jobs', label: 'Browse jobs', icon: 'search' },
        { to: '/seeker/applications', label: 'My applications', icon: 'clipboard' },
        { to: '/seeker/offers', label: 'My offers', icon: 'file' },
        { to: '/seeker/saved', label: 'Saved jobs', icon: 'bookmark' },
        { to: '/seeker/alerts', label: 'Job alerts', icon: 'bell' },
      ],
    },
    {
      label: 'My details',
      items: [
        { to: '/seeker/profile', label: 'Profile', icon: 'user' },
        { to: '/seeker/documents', label: 'Documents', icon: 'file' },
        { to: '/seeker/payslips', label: 'Payslips', icon: 'wallet' },
      ],
    },
  ],

  EMPLOYER: [
    { items: [{ to: '/employer', label: 'Dashboard', icon: 'home', end: true }] },
    {
      label: 'Hiring',
      items: [
        { to: '/employer/jobs', label: 'Vacancies', icon: 'briefcase' },
        { to: '/employer/applicants', label: 'Applicants', icon: 'users' },
        { to: '/employer/pipeline', label: 'Pipeline', icon: 'layers' },
        { to: '/employer/candidates', label: 'Candidates', icon: 'search' },
        { to: '/employer/interviews', label: 'Interviews', icon: 'calendar' },
      ],
    },
    {
      label: 'Workforce',
      items: [
        { to: '/employer/deployments', label: 'Deployments', icon: 'factory' },
        { to: '/employer/attendance', label: 'Attendance', icon: 'calendar' },
        { to: '/employer/invoices', label: 'Invoices', icon: 'invoice' },
        { to: '/employer/reports', label: 'Reports', icon: 'chart' },
        { to: '/employer/company', label: 'Company', icon: 'building' },
      ],
    },
  ],

  STAFF: adminNav(),
  ADMIN: adminNav(),
  SUPER_ADMIN: adminNav(),
};

function adminNav(): NavGroup[] {
  return [
    {
      items: [
        { to: '/admin', label: 'Dashboard', icon: 'home', end: true, permission: 'dashboard:view' },
      ],
    },
    {
      label: 'Verification',
      items: [
        { to: '/admin/verification', label: 'Verification queue', icon: 'clipboard', permission: 'employer:view' },
        { to: '/admin/employers', label: 'Employers', icon: 'building', permission: 'employer:view' },
        { to: '/admin/jobs', label: 'Vacancies', icon: 'briefcase', permission: 'job:view' },
        { to: '/admin/seekers', label: 'Candidates', icon: 'users', permission: 'seeker:view' },
        { to: '/admin/documents', label: 'Documents', icon: 'file', permission: 'document:verify' },
      ],
    },
    {
      label: 'Operations',
      items: [
        {
          to: '/admin/pipeline',
          label: 'Pipeline',
          icon: 'layers',
          permission: 'application:view',
        },
        {
          to: '/admin/requirements',
          label: 'Requirements',
          icon: 'clipboard',
          permission: 'recruitment:manage',
        },
        {
          to: '/admin/deployments',
          label: 'Deployments',
          icon: 'factory',
          permission: 'deployment:manage',
        },
        {
          to: '/admin/attendance',
          label: 'Attendance',
          icon: 'calendar',
          permission: 'attendance:view',
        },
        {
          to: '/admin/invoices',
          label: 'Invoices',
          icon: 'invoice',
          permission: 'billing:view',
        },
        {
          to: '/admin/payroll',
          label: 'Payroll',
          icon: 'wallet',
          permission: 'payroll:view',
        },
      ],
    },
    {
      label: 'Insights',
      items: [{ to: '/admin/reports', label: 'Reports', icon: 'chart', permission: 'report:view' }],
    },
    {
      label: 'Trust & safety',
      items: [
        { to: '/admin/trust', label: 'Fraud reports', icon: 'shield', permission: 'trust:view' },
      ],
    },
    {
      label: 'System',
      items: [
        { to: '/admin/content', label: 'Content', icon: 'layers', permission: 'content:manage' },
        { to: '/admin/staff', label: 'Staff', icon: 'shield', permission: 'staff:manage' },
        { to: '/admin/audit', label: 'Audit log', icon: 'history', permission: 'audit:view' },
      ],
    },
  ];
}

const ROLE_LABEL: Record<UserRole, string> = {
  SUPER_ADMIN: 'Super admin',
  ADMIN: 'Admin',
  STAFF: 'Recruitment desk',
  EMPLOYER: 'Employer',
  SEEKER: 'Job seeker',
};

export function AppShell() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Navigating should close both overlays, or the drawer covers the page the
  // user just asked for.
  useEffect(() => {
    setDrawerOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        setMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!user) return null;

  const groups = NAV_BY_ROLE[user.role]
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          !item.permission ||
          user.role === Role.SUPER_ADMIN ||
          user.permissions.includes(item.permission),
      ),
    }))
    .filter((group) => group.items.length > 0);

  const handleSignOut = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  const displayName = user.fullName ?? user.phone ?? user.email ?? 'Account';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  const sidebar = (
    <nav className="flex h-full flex-col gap-6 overflow-y-auto px-3 py-4" aria-label="Main">
      {groups.map((group, index) => (
        <div key={group.label ?? index}>
          {group.label && (
            <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              {group.label}
            </p>
          )}
          <ul className="space-y-0.5">
            {group.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cx(
                      'flex min-h-8 items-center gap-2.5 border-l-[3px] px-3 py-1.5 text-xs transition-colors',
                      isActive
                        ? 'border-brand-400 bg-brand-600/25 font-semibold text-white'
                        : 'border-transparent text-ink-300 hover:bg-ink-800 hover:text-white',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        name={item.icon}
                        className={isActive ? 'text-white' : 'text-ink-400'}
                      />
                      <span className="truncate">{item.label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    <DensityProvider density="app">
      <div className="min-h-screen bg-ink-50">
        {/* Sidebar — fixed on desktop, a slide-over below lg. */}
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 border-r border-ink-800 bg-ink-900 lg:block">
          <div className="flex h-14 items-center border-b border-ink-800 px-4">
            {/* Stacked lockup: the full name does not fit a 240px rail on one
                line, and truncating a company name reads as a bug. */}
            <Link to="/" className="flex items-center gap-2.5">
              <BrandMark className="h-8 w-8 shrink-0" onDark />
              <span className="min-w-0 leading-tight">
                <span className="block truncate text-sm font-semibold text-white">
                  {SUPPLIER.brandShort}
                </span>
                <span className="block truncate text-[11px] text-ink-400">
                  {SUPPLIER.brandDescriptor}
                </span>
              </span>
            </Link>
          </div>
          <div className="h-[calc(100vh-3.5rem)]">{sidebar}</div>
        </aside>

        {drawerOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div
              className="absolute inset-0 bg-ink-900/50"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            <div className="absolute inset-y-0 left-0 flex w-64 flex-col bg-ink-900 shadow-xl">
              <div className="flex h-14 shrink-0 items-center justify-between border-b border-ink-800 px-4">
                <span className="flex items-center gap-2.5">
                  <BrandMark className="h-8 w-8 shrink-0" onDark />
                  <span className="min-w-0 leading-tight">
                    <span className="block truncate text-sm font-semibold text-white">
                      {SUPPLIER.brandShort}
                    </span>
                    <span className="block truncate text-[11px] text-ink-400">
                      {SUPPLIER.brandDescriptor}
                    </span>
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Close navigation"
                  className="rounded-md p-1.5 text-ink-400 hover:bg-ink-800 hover:text-white"
                >
                  ✕
                </button>
              </div>
              <div className="min-h-0 flex-1">{sidebar}</div>
            </div>
          </div>
        )}

        <div className="lg:pl-60">
          <header className="sticky top-0 z-20 border-b border-ink-200 bg-white">
            <div className="flex h-14 items-center gap-3 px-4">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                aria-label="Open navigation"
                className="rounded-md p-2 text-ink-600 hover:bg-ink-100 lg:hidden"
              >
                <Icon name="menu" size="lg" />
              </button>

              <span className="leading-tight lg:hidden">
                <span className="block text-sm font-semibold text-ink-900">
                  {SUPPLIER.brandShort}
                </span>
                <span className="block text-[11px] text-ink-500">{SUPPLIER.brandDescriptor}</span>
              </span>

              <div className="ml-auto flex items-center gap-1">
                {/* One box over everything the signed-in user can reach, and
                    the Ctrl-K palette behind it. Sits left of the bell: search
                    is initiated, notifications arrive. */}
                <GlobalSearch />
                <NotificationBell />
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setMenuOpen((open) => !open)}
                  aria-expanded={menuOpen}
                  aria-haspopup="menu"
                  className="flex items-center gap-2.5 rounded-md py-1 pl-1 pr-2 text-left hover:bg-ink-100"
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-ink-200 text-xs font-semibold text-ink-700">
                    {initials || '—'}
                  </span>
                  <span className="hidden sm:block">
                    <span className="block max-w-44 truncate text-sm font-medium leading-tight text-ink-900">
                      {displayName}
                    </span>
                    <span className="block text-xs leading-tight text-ink-500">
                      {ROLE_LABEL[user.role]}
                    </span>
                  </span>
                  <Icon name="chevronDown" size="sm" className="text-ink-400" />
                </button>

                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} aria-hidden="true" />
                    <div
                      role="menu"
                      className="absolute right-0 z-20 mt-1 w-56 rounded-lg bg-white py-1 shadow-lg ring-1 ring-ink-200"
                    >
                      <div className="border-b border-ink-100 px-3 py-2">
                        <p className="truncate text-sm font-medium text-ink-900">{displayName}</p>
                        <p className="truncate text-xs text-ink-500">{user.email ?? user.phone}</p>
                      </div>
                      <Link
                        to="/"
                        role="menuitem"
                        className="block px-3 py-2 text-sm text-ink-700 hover:bg-ink-50"
                      >
                        Public site
                      </Link>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={handleSignOut}
                        className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                      >
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </header>

          <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
            <div key={location.pathname} className="motion-safe:animate-fade-in">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </DensityProvider>
  );
}
